import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, List, LogOut, Plus, CheckCircle, Clock, ChevronLeft, ChevronRight, Users, BarChart2, Building2, ArrowLeft } from 'lucide-react';
import { publicaciones } from '../data/mockPublicaciones';

const ITEMS_PER_PAGE = 8;

export default function DemoPublicaciones() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const handleLogout = () => {
    localStorage.removeItem('demo_authenticated');
    navigate('/propuesta/demo');
  };

  const totalPages = Math.ceil(publicaciones.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentPublicaciones = publicaciones.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = [
    { id: 'dashboard',    label: 'Dashboard',    icon: <LayoutDashboard size={15} />, path: '/propuesta/demo/dashboard' },
    { id: 'publicaciones', label: 'Publicaciones', icon: <List size={15} />, path: '/propuesta/demo/publicaciones' },
    { id: 'revendedores',  label: 'Revendedores',  icon: <Users size={15} />, path: '/propuesta/demo/revendedores' },
    { id: 'reportes',      label: 'Reportes',      icon: <BarChart2 size={15} />, path: '/propuesta/demo/reportes' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
              <Building2 className="text-white" size={18} />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Sistema de Tasación</h1>
              <p className="text-xs text-gray-500">Vladimir & Aerocar</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/propuesta')} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition" data-testid="demo-back-to-propuesta" title="Volver a la propuesta">
              <ArrowLeft size={16} /><span className="hidden sm:inline">Propuesta</span>
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
              <LogOut size={16} /><span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 overflow-x-auto">
        <div className="px-4 sm:px-6 py-2 flex items-center gap-1 sm:gap-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition ${tab.id === 'publicaciones' ? 'text-orange-600 bg-orange-50' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}>
              {tab.icon}<span>{tab.label}</span>
            </button>
          ))}
          <button onClick={() => navigate('/propuesta/demo/nueva')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg ml-auto whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
            <Plus size={15} /><span>Nueva</span>
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="px-4 sm:px-6 py-6 sm:py-8 max-w-7xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Todas las Publicaciones
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {publicaciones.length} publicaciones totales
              </p>
            </div>
          </div>

          {/* Tabla Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vehículo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Patente</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Año</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">KM</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ofertas</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPublicaciones.map((pub) => (
                  <tr key={pub.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pub.marca} {pub.modelo}
                      </div>
                      {pub.version && (
                        <div className="text-xs text-gray-500">{pub.version}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                      {pub.patente}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {pub.año}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {pub.kilometraje.toLocaleString()} km
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(pub.fecha).toLocaleDateString('es-UY')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {pub.mercadoLibre && pub.gerencia ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <CheckCircle size={12} />
                          Completa
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          <Clock size={12} />
                          En proceso
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        {pub.revendedores.length}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/propuesta/demo/publicacion/${pub.id}`)}
                        className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Ver detalle →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards Mobile */}
          <div className="md:hidden divide-y divide-gray-200">
            {currentPublicaciones.map((pub) => (
              <div key={pub.id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {pub.marca} {pub.modelo}
                    </h3>
                    <p className="text-sm text-gray-600">{pub.version} · {pub.año}</p>
                  </div>
                  {pub.mercadoLibre && pub.gerencia ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <CheckCircle size={12} />
                      Completa
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      <Clock size={12} />
                      En proceso
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div>
                    <span className="text-gray-500">Patente:</span>
                    <span className="ml-1 font-mono font-medium text-gray-900">{pub.patente}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">KM:</span>
                    <span className="ml-1 font-medium text-gray-900">{pub.kilometraje.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Ofertas:</span>
                    <span className="ml-1 font-medium text-purple-700">{pub.revendedores.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha:</span>
                    <span className="ml-1 font-medium text-gray-900">{new Date(pub.fecha).toLocaleDateString('es-UY')}</span>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/propuesta/demo/publicacion/${pub.id}`)}
                  className="w-full text-sm text-orange-600 hover:text-orange-700 font-medium text-center py-2 border border-orange-200 rounded-lg hover:bg-orange-50 transition"
                >
                  Ver detalle completo →
                </button>
              </div>
            ))}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Mostrando {startIndex + 1} - {Math.min(startIndex + ITEMS_PER_PAGE, publicaciones.length)} de {publicaciones.length}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition ${
                        currentPage === page
                          ? 'bg-orange-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === totalPages
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
